import React, { useCallback, useEffect, useState } from 'react'
import {
  ADMIN_TEST_PUSH_DEFAULT_HEADING,
  ADMIN_TEST_PUSH_DEFAULT_MESSAGE,
  AdminSendTestPushResultDto,
  ApiError,
  NOTIFICATION_PRESET_TRIGGER_CATEGORY_LABELS,
  NOTIFICATION_PRESET_TRIGGER_CATEGORY_ORDER,
  NOTIFICATION_PRESET_TRIGGER_LABELS,
  NOTIFICATION_PRESET_TRIGGER_TO_CATEGORY,
  NotificationPresetTrigger,
  NotificationTriggerDiagnosisDto,
  NotificationTriggerDiagnosisResult,
  RunScheduledPresetsNowResultDto,
  UserDetailDto,
  UserNotificationTriggerDiagnosisDto,
} from '@ivy-monorepo/shared'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import NotificationDevicePreview from '../../components/admin/NotificationDevicePreview'
import { adminService } from '../../services/admin.service'

function triggerLabel(trigger: string): string {
  return NOTIFICATION_PRESET_TRIGGER_LABELS[trigger as NotificationPresetTrigger] ?? trigger
}

/** Groups items by their trigger's category, in category-order, skipping empty categories. */
function groupByTriggerCategory<T>(items: T[], getTrigger: (item: T) => NotificationPresetTrigger) {
  const byCategory = new Map<string, T[]>()
  for (const item of items) {
    const category = NOTIFICATION_PRESET_TRIGGER_TO_CATEGORY[getTrigger(item)]
    const list = byCategory.get(category) ?? []
    list.push(item)
    byCategory.set(category, list)
  }
  return NOTIFICATION_PRESET_TRIGGER_CATEGORY_ORDER
    .filter(category => byCategory.has(category))
    .map(category => ({
      category,
      categoryLabel: NOTIFICATION_PRESET_TRIGGER_CATEGORY_LABELS[category],
      items: byCategory.get(category) as T[],
    }))
}

const DIAGNOSIS_BADGE_COLOR: Record<NotificationTriggerDiagnosisResult, string> = {
  [NotificationTriggerDiagnosisResult.WouldSend]: '#2e7d32',
  [NotificationTriggerDiagnosisResult.Ineligible]: '#9e6a00',
  [NotificationTriggerDiagnosisResult.NoPresetEnabled]: '#999',
}

const DIAGNOSIS_BADGE_LABEL: Record<NotificationTriggerDiagnosisResult, string> = {
  [NotificationTriggerDiagnosisResult.WouldSend]: 'Would send now',
  [NotificationTriggerDiagnosisResult.Ineligible]: 'Ineligible',
  [NotificationTriggerDiagnosisResult.NoPresetEnabled]: 'No preset enabled',
}

const DiagnosisTriggerCard = ({ t }: { t: NotificationTriggerDiagnosisDto }) => (
  <div style={{ border: '1px solid #e2e2e2', borderRadius: '6px', padding: '10px 12px', background: '#fff' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
      <strong style={{ fontSize: '13px' }}>{triggerLabel(t.trigger)}</strong>
      <span
        style={{
          color: '#fff',
          background: DIAGNOSIS_BADGE_COLOR[t.result],
          borderRadius: '10px',
          padding: '2px 10px',
          fontSize: '12px',
          whiteSpace: 'nowrap',
        }}
      >
        {DIAGNOSIS_BADGE_LABEL[t.result]}
      </span>
    </div>
    {t.reasons.length > 0 ? (
      <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '13px', color: '#a45a00' }}>
        {t.reasons.map((reason, i) => (
          <li key={i}>{reason}</li>
        ))}
      </ul>
    ) : null}
    {Object.keys(t.details).length > 0 ? (
      <details style={{ marginTop: '8px', fontSize: '12px', color: '#555' }}>
        <summary style={{ cursor: 'pointer' }}>Details</summary>
        <pre style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{JSON.stringify(t.details, null, 2)}</pre>
      </details>
    ) : null}
  </div>
)

const ScheduledTriggerTestingTools = () => {
  const [isRunning, setIsRunning] = useState(false)
  const [runResult, setRunResult] = useState<RunScheduledPresetsNowResultDto | null>(null)
  const [runError, setRunError] = useState('')

  const [diagUserPublicId, setDiagUserPublicId] = useState('')
  const [isDiagnosing, setIsDiagnosing] = useState(false)
  const [diagnosis, setDiagnosis] = useState<UserNotificationTriggerDiagnosisDto | null>(null)
  const [diagError, setDiagError] = useState('')

  const handleRunNow = async () => {
    setIsRunning(true)
    setRunError('')
    setRunResult(null)
    try {
      const result = await adminService.runScheduledPresetsNow()
      setRunResult(result)
    } catch (err) {
      const apiError = err as ApiError
      setRunError(apiError.friendlyMessage || apiError.message || 'Failed to run scheduled triggers.')
    } finally {
      setIsRunning(false)
    }
  }

  const handleDiagnose = async () => {
    const trimmed = diagUserPublicId.trim()
    if (!trimmed) return
    setIsDiagnosing(true)
    setDiagError('')
    setDiagnosis(null)
    try {
      const result = await adminService.getScheduledTriggerDiagnosis(trimmed)
      setDiagnosis(result)
    } catch (err) {
      const apiError = err as ApiError
      setDiagError(apiError.friendlyMessage || apiError.message || 'Failed to diagnose user.')
    } finally {
      setIsDiagnosing(false)
    }
  }

  const runResultByCategory = runResult
    ? groupByTriggerCategory(
        Object.entries(runResult.byTrigger),
        ([trigger]) => trigger as NotificationPresetTrigger,
      )
    : []

  const diagnosisByCategory = diagnosis ? groupByTriggerCategory(diagnosis.triggers, t => t.trigger) : []

  return (
    <div
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '14px 16px',
        marginTop: '28px',
        background: '#fafafa',
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: '10px', fontSize: '15px' }}>Scheduled trigger tools</h3>
      <p style={{ margin: '0 0 14px', color: '#777', fontSize: '13px' }}>
        Covers every cron-polled trigger: activation gates, habit loop, and resurrection. Real-time
        triggers (likes, messages, check-ins, etc.) fire on the action itself and aren't tested here.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <button type="button" className="styled-button sm" disabled={isRunning} onClick={() => void handleRunNow()}>
          {isRunning ? 'Running…' : 'Run scheduled triggers now'}
        </button>
        <span style={{ color: '#555', fontSize: '13px' }}>
          Fires the real scheduled-preset evaluation immediately (bypasses the job schedule). Safe to
          re-run — sends are deduped per anchor, so nothing goes out twice. Results appear in the
          Notification Log tab.
        </span>
      </div>
      {runError ? <div className="error-message" style={{ marginBottom: '10px' }}>{runError}</div> : null}
      {runResult ? (
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <strong>Sent {runResult.total} notification(s).</strong>
          {runResultByCategory.map(group => (
            <div key={group.category} style={{ marginTop: '6px' }}>
              <div style={{ fontWeight: 600, color: '#555' }}>{group.categoryLabel}</div>
              <ul style={{ margin: '2px 0 0', paddingLeft: '18px' }}>
                {group.items.map(([trigger, count]) => (
                  <li key={trigger}>
                    {triggerLabel(trigger)}: {count}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}

      <hr style={{ border: 'none', borderTop: '1px solid #e2e2e2', margin: '14px 0' }} />

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={diagUserPublicId}
          onChange={event => setDiagUserPublicId(event.target.value)}
          placeholder="User public ID"
          style={{ padding: '8px 10px', minWidth: '220px' }}
        />
        <button
          type="button"
          className="styled-button sm"
          disabled={isDiagnosing || !diagUserPublicId.trim()}
          onClick={() => void handleDiagnose()}
        >
          {isDiagnosing ? 'Checking…' : 'Diagnose trigger eligibility'}
        </button>
        <span style={{ color: '#555', fontSize: '13px' }}>
          Read-only — checks would-it-send-right-now for every scheduled trigger, nothing is sent.
        </span>
      </div>
      {diagError ? <div className="error-message" style={{ marginTop: '10px' }}>{diagError}</div> : null}

      {diagnosis ? (
        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {diagnosisByCategory.map(group => (
            <div key={group.category}>
              <div style={{ fontWeight: 600, color: '#555', fontSize: '13px', marginBottom: '6px' }}>
                {group.categoryLabel}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {group.items.map(t => (
                  <DiagnosisTriggerCard key={t.trigger} t={t} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

const NotificationsTestPushTab = () => {
  const [publicId, setPublicId] = useState('')
  const [user, setUser] = useState<UserDetailDto | null>(null)
  const [message, setMessage] = useState(ADMIN_TEST_PUSH_DEFAULT_MESSAGE)
  const [error, setError] = useState('')
  const [sendResult, setSendResult] = useState<AdminSendTestPushResultDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const loadUser = useCallback(async () => {
    const trimmed = publicId.trim()
    if (!trimmed) {
      setError('Enter a user or account public ID.')
      return
    }

    setIsLoading(true)
    setError('')
    setSendResult(null)
    try {
      const data = await adminService.getUserDetail(trimmed)
      setUser(data)
    } catch (err) {
      setUser(null)
      const apiError = err as ApiError
      setError(apiError.friendlyMessage || apiError.message || 'Failed to load user.')
    } finally {
      setIsLoading(false)
    }
  }, [publicId])

  useEffect(() => {
    setSendResult(null)
  }, [publicId])

  const handleResetMessage = () => {
    setMessage(ADMIN_TEST_PUSH_DEFAULT_MESSAGE)
  }

  const handleConfirmSend = async () => {
    const trimmed = message.trim()
    const targetPublicId = user?.publicId ?? publicId.trim()
    if (!trimmed) {
      setError('Message cannot be empty.')
      return false
    }
    if (!targetPublicId) {
      setError('Load a user first.')
      return false
    }

    setIsSending(true)
    setError('')
    setSendResult(null)
    try {
      const result = await adminService.sendTestPush(targetPublicId, trimmed)
      setSendResult(result)
      return true
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.friendlyMessage || apiError.message || 'Failed to send test push.')
      return false
    } finally {
      setIsSending(false)
    }
  }

  const userName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.publicId
    : ''
  const canSend =
    !!user &&
    user.hasValidOneSignalToken &&
    message.trim().length > 0 &&
    !isLoading &&
    !isSending

  return (
    <div className="notifications-test-push-tab">
      <p style={{ marginBottom: '16px', color: '#555' }}>
        Send a visible OneSignal push to one user to confirm delivery. The user must have a valid
        device token stored on their profile.
      </p>

      {error ? (
        <div className="error-message" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      ) : null}

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="test-push-user-public-id" style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>
          User public ID
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            id="test-push-user-public-id"
            type="text"
            value={publicId}
            onChange={event => setPublicId(event.target.value)}
            placeholder="Account or user public ID"
            style={{ flex: 1, padding: '10px', boxSizing: 'border-box' }}
            disabled={isLoading || isSending}
          />
          <button
            type="button"
            className="styled-button sm"
            disabled={isLoading || isSending || !publicId.trim()}
            onClick={() => void loadUser()}
          >
            {isLoading ? 'Loading…' : 'Load user'}
          </button>
        </div>
      </div>

      {user ? (
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '12px 14px',
            background: '#fafafa',
            marginBottom: '24px',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '8px' }}>{userName}</div>
          <div style={{ color: '#555', fontSize: '14px' }}>
            Account: {user.publicId}
            {user.userPublicId ? ` · Profile: ${user.userPublicId}` : ''}
          </div>
          <div style={{ color: '#555', fontSize: '14px', marginTop: '4px' }}>
            OneSignal token:{' '}
            {user.hasValidOneSignalToken
              ? 'Ready'
              : user.hasDeviceToken
                ? 'Invalid'
                : 'Missing'}
          </div>
        </div>
      ) : null}

      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label htmlFor="admin-test-push-tab-message" style={{ fontWeight: 600 }}>
            Message
          </label>
          <button type="button" className="link-button" style={{ marginLeft: 0 }} onClick={handleResetMessage}>
            Reset to default
          </button>
        </div>
        <textarea
          id="admin-test-push-tab-message"
          value={message}
          onChange={event => setMessage(event.target.value)}
          rows={4}
          style={{ width: '100%', padding: '10px', resize: 'vertical', boxSizing: 'border-box' }}
        />
      </div>

      <NotificationDevicePreview message={message} heading={ADMIN_TEST_PUSH_DEFAULT_HEADING} />

      <div style={{ marginBottom: '16px' }}>
        <button
          type="button"
          className="styled-button"
          disabled={!canSend}
          onClick={() => setIsConfirmOpen(true)}
        >
          Send test push
        </button>
      </div>

      {sendResult ? (
        <div style={{ marginBottom: '16px', color: '#2e7d32' }}>
          Sent. OneSignal id: {sendResult.oneSignalNotificationId}
          {typeof sendResult.recipients === 'number' ? ` · Recipients: ${sendResult.recipients}` : ''}
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => {
          if (!isSending) {
            setIsConfirmOpen(false)
          }
        }}
        onConfirm={handleConfirmSend}
        title="Send test push?"
        confirmLabel="Send"
        isLoading={isSending}
        confirmDisabled={!canSend}
      >
        <p>
          This will send a visible OneSignal notification to <strong>{userName || 'this user'}</strong>.
        </p>
        <p style={{ marginTop: '12px', whiteSpace: 'pre-wrap' }}>
          Message:
          {'\n'}
          {message.trim()}
        </p>
      </ConfirmationModal>

      <ScheduledTriggerTestingTools />
    </div>
  )
}

export default NotificationsTestPushTab
